/**
 * ImageSwap class
 * Changes attributes on mousein/mouseout
 *
 * Markup example:
 * ```
 * <div data-en-image-swap='{
 *     "scope": ".some-parent", // scope selector (will receive active class)
 *     "target": ".some-element", // element selector (to apply attributes to)
 *     "attribute": "id", // attribute to use (eg. "id", "data-some-attribute", "src", …)
 *     "value": "some-value", // value to set
 * }'></div>
 * ```
 *
 * Implementation example:
 * ```
 * var imageSwapper = new ImageSwap();
 * ```
 *
 * @class ImageSwap
 * @author EN GARDE (David Hell)
 * @version 2.0.0
 */

import merge from 'lodash/merge';

class ImageSwap {

	static _defaults = {
		attribute: 'data-en-image-swap',
		classNames: {
			selected: 'en-is-selected',
			active: 'en-is-active'
		}
	};

	/**}
   * Constructor
   * @constructor
   * @param {object} [options]
   * @property {string} attribute="data-en-image-swap"
   * @property {object} classNames
   * @property {string} classNames.selected="en-is-selected" – added to the toggle element
   * @property {string} classNames.active="en-is-active" – added to the scope element
   */
	constructor( options ) {
		this.options = merge( {}, ImageSwap._defaults, options );

		document.body.addEventListener( 'mouseenter', ( evt ) => this.handleMouseEvent( evt, true ), true );
		document.body.addEventListener( 'mouseleave', ( evt ) => this.handleMouseEvent( evt, false ), true );
	}

	handleMouseEvent( evt, toggleState = false ) {

		if ( evt.target && evt.target.matches( '[' + this.options.attribute + ']' ) ) {
			const triggerEl = evt.target;

			// Cancel swap when the state is set to active.
			if ( triggerEl.classList.contains( this.options.classNames.active ) ) {
				return;
			}

			this.swap( triggerEl, toggleState );
			triggerEl.classList.toggle( this.options.classNames.selected, toggleState );
		}
	}

	/**
   * Swap
   * @memberof ImageSwap
   */
	swap( triggerEl, active = false ) {

		// Get config from triggerEl.
		const config = JSON.parse( triggerEl.getAttribute( this.options.attribute ) );

		// Find all scopes.
		const scopeEls = document.querySelectorAll( config.scope );
		scopeEls.forEach( ( scopeEl ) => {

			// Find all images in scope.
			const targetEls = scopeEl.querySelectorAll( config.target );
			targetEls.forEach( ( targetEl ) => {
				if ( targetEl.id === config.value ) {
					// Hide Image if needed.
					targetEl.classList.toggle( 'en_hidden', ! active );
				}
			} );

			// Add active class to scope.
			scopeEl.classList.toggle( this.options.classNames.active, active );
		} );
	}
}

export default ImageSwap;
